import { Grid } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { createInvoiceArticleCategory } from 'src/shared/services/invoiceArticleCategoryService';
import { InvoiceArticleCategoryRequest } from '../models/invoiceArticleCategoryRequest';

type CreateArticleCategoryProps = {
  setOpenCreateArticle: (openCreateArticle: boolean) => void;
  setRefetchDataGrid: (refetch: boolean) => void;
};

export const CreateArticleCategory = (props: CreateArticleCategoryProps) => {
  const { setOpenCreateArticle, setRefetchDataGrid } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);

  const minimumCharRequired = 3;
  const maximumCharRequired = 20;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const maxCharRequiredMessage = t('common:validationMessages.maxLength', { count: maximumCharRequired });

  const schema = yup.object({
    articleCategory: yup.string().min(minimumCharRequired, minCharRequiredMessage).max(maximumCharRequired, maxCharRequiredMessage).required(),
  });

  const formMethods = useForm<InvoiceArticleCategoryRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      product_id: 0,
      articleCategory: '',
    },
  });
  const { handleSubmit } = formMethods;

  const handleClose = () => {
    setOpenCreateArticle(false);
  };

  const onSubmit = async (data: InvoiceArticleCategoryRequest) => {
    setLoading(true);

    try {
      await createInvoiceArticleCategory(data);
      if (isMounted()) {
        toast.success(<>{t('invoice:articleRegister.createSuccess')}</>);
        handleClose();
        setRefetchDataGrid(true);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.success(<>{t('invoice:articleRegister.error')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GenericDialog
          maxWidth='sm'
          open
          onClose={handleClose}
          onSave={handleSubmit(onSubmit)}
          dialogTitle={t('invoice:articleCategoryPage.createCategory')}
          dialogContent={
            <Grid container spacing={5}>
              <LoadingOverlay loading={loading} />
              <Grid item xs={12}>
                <ControlledTextField label={t('invoice:articleCategoryPage.categoryName')} fullWidth name='articleCategory' />
              </Grid>
            </Grid>
          }
        />
      </form>
    </FormProvider>
  );
};
